import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { DocumentArrowUpIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/16/solid";

const FormCreateATicket = ({
  formRef,
  handleChange,
  handleFileChange,
  handleSubmit,
  handleFormReset,
  formData,
  files,
  getRootProps,
  getInputProps,
  isDragActive,
  handleRecaptchaChange,
  companyOptions,
  showSuggestions,
  setFormData,
  setShowSuggestions,
  contactOptions,
  showSuggestionsFN,
  setShowSuggestionsFN,
  highlightCompanyIndex,
  setHighlightCompanyIndex,
  highlightContactIndex,
  setHighlightContactIndex,
  handleEmailInputChange,
  itemRefs
}) => {
  return (
    <form ref={formRef} name="form1" onSubmit={handleSubmit} className="mx-auto max-w-7xl px-4 py-6">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-0">
          <tbody>
            {/* Client Info Section */}
            <tr>
                <th className="hidden sm:table-cell text-left p-3 align-top text-base font-extrabold w-auto sm:w-[20%]">Client Info</th>
                <td className="text-left p-3">
                  <div className="sm:hidden text-base w-auto font-extrabold mb-4">Client Info</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <div className="mb-4">For existing customers, contact information may be auto-filled with asterisks (*) to mask personal data pulled from our records. You don’t need to correct or update them.</div>
                      <label>Company Name *</label>
                      <input
                        id="companyName"
                        name="companyName"
                        type="text"
                        value={formData.companyName}
                        onChange={handleChange}
                        onFocus={handleChange}
                        onBlur={() => setTimeout(() => {
                          setShowSuggestions(false);
                          setHighlightContactIndex(-1);
                        }, 150)}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setHighlightCompanyIndex((prev) => Math.min(prev + 1, companyOptions.length - 1));
                          } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setHighlightCompanyIndex((prev) => Math.max(prev - 1, 0));
                          } else if (e.key === "Enter" && highlightCompanyIndex >= 0) {
                            const selected = companyOptions[highlightCompanyIndex];
                            setFormData({ ...formData, companyName: selected.Name, companyId: selected.Id });
                            setShowSuggestions(false);
                            setHighlightCompanyIndex(-1);
                          } else if (e.key === "Escape") {
                            setShowSuggestions(false);
                            setHighlightCompanyIndex(-1);
                          }
                        }}
                        required
                        className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                      />
                      {showSuggestions && companyOptions.length > 0 && (
                        <ul className="absolute z-10 mt-1 max-h-60 w-auto overflow-auto rounded-md border-white border-2 bg-black py-1 text-base shadow-lg ring-1 ring-white ring-opacity-5 focus:outline-none sm:text-sm">
                          {companyOptions.map((option, index) => (
                            <li
                              key={option.Id}
                              ref={(el) => (itemRefs.current[index] = el)}
                              onMouseDown={() => {
                                setFormData({ ...formData, companyName: option.Name, companyId: option.Id });                                
                                setShowSuggestions(false);
                                setHighlightCompanyIndex(-1);
                              }}
                              className={`cursor-pointer select-none px-4 py-2 text-gray-100 ${
                                highlightCompanyIndex === index ? "bg-blue-800" : "hover:bg-blue-800"
                              }`}
                            >
                              {option.Name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <label>First Name *</label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        autoComplete="given-name"
                        value={formData.firstName}
                        onChange={handleChange}
                        onFocus={handleChange}
                        onBlur={() => setTimeout(() => {
                          setShowSuggestionsFN(false);
                          setHighlightContactIndex(-1);
                        }, 150)}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setHighlightContactIndex((prev) => Math.min(prev + 1, contactOptions.length - 1));
                          } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setHighlightContactIndex((prev) => Math.max(prev - 1, 0));
                          } else if (e.key === "Enter" && highlightContactIndex >= 0) {
                            const selected = contactOptions[highlightContactIndex];
                            const selectedEmail = selected.Email;
                            setFormData({
                              ...formData,
                              firstName: selected.FirstName,
                              lastName: selected.LastName.charAt(0) + "*".repeat(selected.LastName.length - 1),
                              email: selectedEmail,
                              emailInput: (() => {
                                const [local, domain] = selectedEmail.split("@");
                                const visibleLocal = local.slice(0, 2);
                                const visibleDomain = domain?.slice(0, 1) || "";
                                return `${visibleLocal}***@${visibleDomain}*******`;
                              })(),
                              isEmailMasked: true,
                              contactId: selected.Id,
                            });
                            setShowSuggestionsFN(false);
                            setHighlightContactIndex(-1);
                          } else if (e.key === "Escape") {
                            setShowSuggestionsFN(false);
                            setHighlightContactIndex(-1);
                          }
                        }}
                        required
                        className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                      />
                      {showSuggestionsFN && contactOptions.length > 0 && (
                        <ul className="absolute z-10 mt-1 max-h-60 w-auto overflow-auto rounded-md border-white border-2 bg-black py-1 text-base shadow-lg ring-1 ring-white ring-opacity-5 focus:outline-none sm:text-sm">
                          {contactOptions.map((option, index) =>
                            option?.FirstName ? (
                            <li
                              key={option.Id}
                              ref={(el) => (itemRefs.current[index] = el)}
                              onMouseDown={() => {
                                const selectedEmail = option.Email;
                                setFormData({ ...formData,
                                  firstName: option.FirstName,
                                  lastName: option.LastName.charAt(0) + "*".repeat(option.LastName.length - 1),
                                  email: selectedEmail,
                                  emailInput: (() => {
                                    const [local, domain] = selectedEmail.split("@");
                                    const visibleLocal = local.slice(0, 2);
                                    const visibleDomain = domain?.slice(0, 1) || '';
                                    return `${visibleLocal}***@${visibleDomain}*******`;
                                  })(),
                                  isEmailMasked: true,
                                  contactId: option.Id });
                                setShowSuggestionsFN(false);
                                setHighlightContactIndex(-1);
                              }}
                              className={`cursor-pointer select-none px-4 py-2 text-gray-100 ${
                                highlightContactIndex === index ? "bg-blue-800" : "hover:bg-blue-800"
                              }`}
                            >
                              {option.FirstName}{" "}
                              {option.LastName.charAt(0) + "x".repeat(option.LastName.length - 1)}
                            </li>
                            ) : null
                          )}
                        </ul>
                      )}
                    </div>
                    <div>
                      <label>Last Name *</label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        autoComplete="family-name"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label>Email *</label>
                      <input
                        id="email"
                        name="email"
                        type="text"
                        autoComplete="email"
                        value={formData.emailInput}
                        onChange={handleEmailInputChange}
                        required
                        className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                      />
                    </div>
                  </div>
                </td>
            </tr>

            {/* Service Info Section */}
            <tr>
                <th className="hidden sm:table-cell text-left p-3 align-top text-base font-extrabold border-t border-slate-700 w-auto sm:w-[20%]">Service Info</th>
                <td className="text-left p-3 border-t border-slate-700">
                  <div className="sm:hidden text-base w-auto font-extrabold mb-4">Service Info</div>  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label>Summary *</label>
                      <input
                        id="summary"
                        name="summary"
                        type="text"
                        value={formData.summary}
                        onChange={handleChange}
                        required
                        className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label>Description *</label>
                      <textarea
                        id="description"
                        name="description"
                        rows={3}
                        value={formData.description}
                        onChange={handleChange}
                        required
                        className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                      />
                    </div>
                    <div>
                      <label>Charger Type</label>
                      <div className="mt-2 grid grid-cols-1">
                        <select
                          id="chargerType"
                          name="chargerType"
                          value={formData.chargerType}
                          onChange={handleChange}
                          className="col-start-1 row-start-1 w-full appearance-none rounded-md py-1.5 pr-8 pl-3 text-base bg-black text-gray-100 outline outline-2 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                        >
                            <option value="Artemis">Artemis</option>
                            <option value="Athena">Athena</option>
                            <option value="Neptune">Neptune</option>
                            <option value="Titan 180">Titan 180</option>
                            <option value="Titan 320">Titan 320</option>
                            <option value="Venus">Venus</option>
                            <option value="Other">Other</option>
                        </select>
                        <ChevronDownIcon
                                                aria-hidden="true"
                                                className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                                                />
                      </div>
                    </div>
                    <div className="sm:col-span-2"></div>
                    <div className='sm:col-span-2'>
                                        <label>Photo of Name Plate / Serial Number</label>
                                        <div
                                            {...getRootProps()}
                                            className={`mt-2 flex justify-center rounded-lg border border-dashed px-6 py-10 cursor-pointer ${
                                            isDragActive ? "border-blue-500 bg-blue-100" : "border-gray-100"
                                            }`}
                                        >
                                            <div className="text-center">
                                              <DocumentArrowUpIcon aria-hidden="true" className="mx-auto size-12 text-gray-600" />
                                              <div className="mt-4 flex text-sm/6 text-gray-600">
                                                  <label
                                                  htmlFor="file-upload"
                                                  className="relative cursor-pointer rounded-md font-semibold text-gray-300 focus-within:ring-2 focus-within:ring-gray-500 focus-within:ring-offset-2 focus-within:outline-hidden hover:text-gray-500">
                                                  <span>Upload a file</span>
                                                  <input {...getInputProps()} className="sr-only" onChange={handleFileChange} />
                                                  </label>
                                                  <p className="pl-1">or drag and drop</p>
                                              </div>
                                              <p className="text-xs/5 text-gray-600">Up to 10MB</p>
                                            </div>
                                        </div>
                                        {/* Preview of uploaded images */}
                                        {files.length > 0 && (
                                            <div className="mt-4">
                                            {files.map((file) => (
                                                <div key={file.name} className="relative">
                                                <img
                                                    src={file.preview}
                                                    alt={file.name}
                                                    className="h-24 object-cover rounded-lg"
                                                    onLoad={() => URL.revokeObjectURL(file.preview)}
                                                />
                                                </div>
                                            ))}
                                            </div>
                                        )}
                                        </div>
                  </div>
                </td>
            </tr>
            <tr>
                <th className="hidden sm:table-cell text-left p-3 align-top text-base font-extrabold border-t border-slate-700 w-auto sm:w-[20%]">Address of Charger</th>
                <td className="text-left p-3 border-t border-slate-700">
                  <div className="sm:hidden text-base w-auto font-extrabold mb-4">Address of Charger</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className='sm:col-span-2'>
                        <label>Country</label>
                        <div className="mt-2 grid grid-cols-1">
                            <select
                                id="country"
                                name="country"
                                autoComplete="country-name"
                                onChange={handleChange}
                                className="col-start-1 row-start-1 w-full appearance-none rounded-md py-1.5 pr-8 pl-3 text-base bg-black text-gray-100 outline outline-2 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500">
                                <option value="US">United States</option>
                                <option value="CA">Canada</option>
                                </select>
                                <ChevronDownIcon
                                aria-hidden="true"
                                className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                            />
                        </div>
                    </div>
                    <div></div>
                    <div className='sm:col-span-3'>
                        <label>Street Address</label>
                        <input
                            id="streetAddress"
                            name="streetAddress"
                            type="text"
                            autoComplete="street-address"
                            onChange={handleChange}
                            className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                        />
                    </div>
                    <div>
                        <label>City</label>
                        <input
                            id="city"
                            name="city"
                                type="text"
                                    autoComplete="address-level2"
                                    onChange={handleChange}
                                    className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                                />
                    </div>
                    <div>
                                <label>State / Province</label>
                                <input
                                    id="state"
                                    name="state"
                                    type="text"
                                    autoComplete="address-level1"
                                    onChange={handleChange}
                                    className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                                />
                    </div>
                    <div>
                                <label>ZIP / Postal Code</label>
                                <input
                                    id="zip"
                                    name="zip"
                                    type="text"
                                    autoComplete="postal-code"
                                    onChange={handleChange}
                                    className="block w-full rounded-md bg-black px-3 py-1.5 text-base text-gray-100 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-orange-500"
                                />
                    </div>                    
                </div>
              </td>
            </tr>            
            {/* Submit & Reset Section */}
            <tr>
                <th className="hidden sm:table-cell text-left p-3 align-top text-base border-t border-slate-700 w-auto sm:w-[20%]"></th>
                <td className="text-right p-3 pb-20 border-t border-slate-700">
                    <ReCAPTCHA
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={handleRecaptchaChange}
                    /><br />
                    <button onClick={handleFormReset} type="reset" className="px-4 py-2 w-full sm:w-auto border border-gray-200 rounded-md text-white bg-black hover:bg-orange-600">
                        Reset
                    </button>
                    &nbsp;
                    <button type="submit" className="px-4 py-2 w-full sm:w-auto border border-transparent rounded-md text-white bg-orange-500 hover:bg-orange-600">
                        Submit
                    </button>
                </td>
            </tr>
          </tbody>
        </table>
      </div>
    </form>
  );
};

export default FormCreateATicket;
